import { Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { fetchClassifications } from "store/slices/classificationSlice";

import { delayCall } from "api/util";
import { AuthButton } from "common";
import { CartIndicator } from "components/cart";
import {
  DesktopNav,
  Logo,
  MobileDrawer,
  SearchBar,
} from "components/navigation";

import { Icon, IconButton } from "@/design-system";

export default function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const dispatch = useDispatch();
  const { classes: categories = [] } = useSelector(
    (state) => state.classification || {}
  );

  useEffect(() => {
    if (categories.length) return undefined;
    return delayCall(() => dispatch(fetchClassifications()));
  }, [dispatch, categories.length]);

  const navCategories = categories
    .filter((category) =>
      Array.isArray(category.child) ? category.child.length > 0 : true
    )
    .map((category) => ({
      name: category.name,
      id: category.id,
      subcategories: Array.isArray(category.child)
        ? category.child.map((subcategory) => ({
            name: subcategory.name,
            id: subcategory.id,
          }))
        : [],
    }));

  const toggleMenu = (key) =>
    setExpandedMenus((menus) => ({ ...menus, [key]: !menus[key] }));

  return (
    <>
      <MobileDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        classification={navCategories}
        expandedMenus={expandedMenus}
        toggleMenu={toggleMenu}
      />
      {drawerOpen && (
        <button
          type="button"
          aria-label="Close navigation menu"
          className="fixed inset-0 z-[var(--z-overlay)] cursor-default border-0 bg-[var(--surface-overlay)]"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <header className="sticky top-0 z-[var(--z-sticky)] bg-card shadow-sm">
        <div className="bg-blue-900 font-sans text-sm text-blue-100">
          <div className="mx-auto flex max-w-container items-center justify-between gap-[var(--space-4)] px-[var(--gutter)] py-[5px]">
            <span className="inline-flex min-w-0 items-center gap-[7px]">
              <Icon name="local_shipping" size={16} weight={400} />
              <span className="truncate">
                Free 1–2 day delivery across Canada on orders over $300
              </span>
            </span>
            <div className="hidden items-center gap-[var(--space-6)] md:flex">
              <a
                href="tel:+18003368322"
                className="inline-flex items-center gap-[6px] whitespace-nowrap text-blue-100 hover:text-white"
              >
                <Icon name="support_agent" size={16} weight={400} />
                1-800-336-8322
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-[6px] whitespace-nowrap text-blue-100 hover:text-white"
              >
                <Icon name="person_search" size={16} weight={400} />
                Find your rep
              </Link>
            </div>
          </div>
        </div>

        <div className="mx-auto flex max-w-container items-center gap-[var(--space-3)] px-[var(--gutter)] py-[10px] lg:gap-[var(--space-6)]">
          <IconButton
            icon={<Icon name="menu" size={22} weight={500} />}
            variant="outline"
            size="sm"
            aria-label="Open navigation menu"
            className="lg:hidden"
            onClick={() => setDrawerOpen(true)}
          />

          {/* The reference's back/forward history pair sat here (`.nav-hist-btn`).
              Removed on request — the browser's own controls cover it. If it ever
              returns, `hooks/useHistoryNav.js` is the state half; see the dead list in
              `dnd-frontend-ui/references/deprecated.md`. */}
          <Logo />

          <div className="relative hidden max-w-[880px] flex-1 lg:block">
            <SearchBar />
          </div>

          <div className="ml-auto flex items-center gap-[var(--space-2)] lg:gap-[var(--space-3)]">
            <div className="hidden lg:block">
              <Suspense fallback={null}>
                <AuthButton />
              </Suspense>
            </div>
            <CartIndicator />
          </div>
        </div>

        <div className="mx-auto max-w-container px-[var(--gutter)] pb-[10px] lg:hidden">
          <SearchBar />
        </div>

        <DesktopNav classification={navCategories} />
      </header>
    </>
  );
}
